import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import Select from 'react-select';
import MaskedInput from 'react-text-mask';
import { selectStylesSm } from '~/Styles/reactSelectStyles';
import { extrairMensagemErro } from '~/Utils/Helpers';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { MASK_VALIDADE_CARTAO, MASK_NUMERO_CARTAO } from '~/constant';
import {
  listarGruposEmpresaDeCartao,
  listarBandeirasCartao,
  incluirCartaoCredito,
  alterarCartaoCredito,
} from '~/Services/Service';
import { cartaoCreditoSchema } from '../validationSchema';
import { TIPOS_CARTAO, STATUS_CARTAO, DIAS_VENCIMENTO } from '../constants';

const DEFAULTS = {
  numero_cartao: '',
  codigo_bandeira: '',
  validade: '',
  nome: '',
  dia_vencimento: 1,
  tipo: 'E',
  codigo_grupo_empresa: '',
  limite_arredondamento: 0,
  status: 'A',
};

// Banco grava número sem máscara (ADR-001). Reaplica grupos de 4 dígitos
// ao carregar pra edição, senão a regex Yup `#### #### #### ####` rejeita
// e o usuário recebe "Formato inválido" mesmo sem ter tocado no campo.
const formatarNumeroCartao = (numero) => {
  if (!numero) return '';
  const limpo = String(numero).replace(/\D/g, '');
  return limpo.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
};

const buildInitialValues = (cartao) => {
  if (!cartao) return DEFAULTS;
  return {
    numero_cartao: formatarNumeroCartao(cartao.numero_cartao),
    codigo_bandeira: cartao.codigo_bandeira || '',
    validade: cartao.validade || '',
    nome: cartao.nome || '',
    dia_vencimento: cartao.dia_vencimento || 1,
    tipo: cartao.tipo || 'E',
    codigo_grupo_empresa: cartao.codigo_grupo_empresa || '',
    limite_arredondamento: cartao.limite_arredondamento != null ? cartao.limite_arredondamento : 0,
    status: cartao.status || 'A',
  };
};

const ModalCartao = ({ show, onHide, cartao, onSaved }) => {
  const ehEdicao = cartao != null;

  const [bandeiras, setBandeiras] = useState([]);
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [loadingOpcoes, setLoadingOpcoes] = useState(false);
  const [confirmInativacao, setConfirmInativacao] = useState({ show: false, submitFn: null });

  useEffect(() => {
    if (show) {
      carregarOpcoes();
    }
  }, [show]);

  const carregarOpcoes = async () => {
    setLoadingOpcoes(true);
    try {
      const [bands, ges] = await Promise.all([
        listarBandeirasCartao(),
        listarGruposEmpresaDeCartao(),
      ]);
      setBandeiras(bands || []);
      // W-0527 (4ª passada): em edição, se a GE do cartão não está mais
      // na listagem (Status='I'), adicionamos uma entrada sintética somente-leitura
      // para o Select preservar a seleção e o BE não rejeitar como "fora do acesso".
      const listaGE = ges || [];
      if (cartao && cartao.codigo_grupo_empresa) {
        const presente = listaGE.some(
          (g) => g.codigo === cartao.codigo_grupo_empresa
        );
        if (!presente) {
          listaGE.push({
            codigo: cartao.codigo_grupo_empresa,
            apelido: `${cartao.apelido_grupo_empresa || 'GE'} (inativa)`,
            inativa: true,
          });
        }
      }
      setGruposEmpresa(listaGE);
    } catch (err) {
      toast.error(extrairMensagemErro(err, 'Erro ao carregar opções.'));
    } finally {
      setLoadingOpcoes(false);
    }
  };

  const opsBandeiras = bandeiras.map((b) => ({ value: b.codigo, label: b.descricao }));
  const opsGE = gruposEmpresa.map((g) => ({
    value: g.codigo,
    label: g.apelido,
    isDisabled: !!g.inativa,
  }));

  const doSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        numero_cartao: values.numero_cartao.replace(/\s/g, ''),
        codigo_bandeira: values.codigo_bandeira,
        validade: values.validade,
        nome: values.nome,
        dia_vencimento: values.dia_vencimento,
        tipo: values.tipo,
        codigo_grupo_empresa: values.codigo_grupo_empresa,
        limite_arredondamento: values.limite_arredondamento != null ? values.limite_arredondamento : 0,
        status: values.status,
      };

      if (ehEdicao) {
        await alterarCartaoCredito({ ...payload, codigo: cartao.codigo });
        toast.success('Cartão atualizado com sucesso.');
      } else {
        await incluirCartaoCredito(payload);
        toast.success('Cartão incluído com sucesso.');
      }
      onSaved();
    } catch (err) {
      toast.error(extrairMensagemErro(err, 'Erro ao salvar cartão.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (values, formikHelpers) => {
    // T11: confirmação ao inativar cartão ativo (A→I)
    const inativando = ehEdicao && cartao.status === 'A' && values.status === 'I';
    if (inativando) {
      setConfirmInativacao({
        show: true,
        submitFn: () => doSubmit(values, formikHelpers),
      });
      return;
    }
    doSubmit(values, formikHelpers);
  };

  const handleConfirmarInativacao = (confirmado) => {
    const { submitFn } = confirmInativacao;
    setConfirmInativacao({ show: false, submitFn: null });
    if (confirmado && submitFn) submitFn();
  };

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{ehEdicao ? 'Alterar Cartão de Crédito' : 'Novo Cartão de Crédito'}</Modal.Title>
        </Modal.Header>

        <Formik
          initialValues={buildInitialValues(cartao)}
          validationSchema={cartaoCreditoSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit: formikSubmit, setFieldValue, isSubmitting }) => (
            <Form onSubmit={formikSubmit} noValidate>
              <Modal.Body>
                {loadingOpcoes && <p className="text-center text-muted">Carregando opções...</p>}

                <Row>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="small mb-1">Número do Cartão *</Form.Label>
                      <MaskedInput
                        name="numero_cartao"
                        type="tel"
                        guide={false}
                        mask={MASK_NUMERO_CARTAO}
                        value={values.numero_cartao}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="#### #### #### ####"
                        className={`form-control${
                          touched.numero_cartao && errors.numero_cartao ? ' is-invalid' : ''
                        }`}
                      />
                      {touched.numero_cartao && errors.numero_cartao && (
                        <div className="invalid-feedback d-block">
                          {errors.numero_cartao}
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="small mb-1">Bandeira *</Form.Label>
                      <Select
                        options={opsBandeiras}
                        styles={selectStylesSm}
                        placeholder="Selecione..."
                        value={opsBandeiras.find((o) => o.value === values.codigo_bandeira) || null}
                        onChange={(opt) => setFieldValue('codigo_bandeira', opt ? opt.value : '')}
                        onBlur={handleBlur}
                        noOptionsMessage={() => 'Nenhuma opção'}
                      />
                      {touched.codigo_bandeira && errors.codigo_bandeira && (
                        <div className="text-danger small mt-1">{errors.codigo_bandeira}</div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="small mb-1">Validade (MM/AA) *</Form.Label>
                      <MaskedInput
                        name="validade"
                        type="tel"
                        guide={false}
                        mask={MASK_VALIDADE_CARTAO}
                        value={values.validade}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="MM/AA"
                        className={`form-control${
                          touched.validade && errors.validade ? ' is-invalid' : ''
                        }`}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.validade}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="small mb-1">Dia de Vencimento *</Form.Label>
                      <Select
                        options={DIAS_VENCIMENTO}
                        styles={selectStylesSm}
                        placeholder="Selecione..."
                        value={DIAS_VENCIMENTO.find((o) => o.value === values.dia_vencimento) || null}
                        onChange={(opt) => setFieldValue('dia_vencimento', opt ? opt.value : '')}
                        onBlur={handleBlur}
                        noOptionsMessage={() => 'Nenhuma opção'}
                      />
                      {touched.dia_vencimento && errors.dia_vencimento && (
                        <div className="text-danger small mt-1">{errors.dia_vencimento}</div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="small mb-1">Tipo *</Form.Label>
                      <Select
                        options={TIPOS_CARTAO}
                        styles={selectStylesSm}
                        placeholder="Selecione..."
                        value={TIPOS_CARTAO.find((o) => o.value === values.tipo) || null}
                        onChange={(opt) => setFieldValue('tipo', opt ? opt.value : '')}
                        onBlur={handleBlur}
                        noOptionsMessage={() => 'Nenhuma opção'}
                      />
                      {touched.tipo && errors.tipo && (
                        <div className="text-danger small mt-1">{errors.tipo}</div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} md={8} className="mb-3">
                    <Form.Group>
                      <Form.Label className="small mb-1">Nome do Titular *</Form.Label>
                      <Form.Control
                        name="nome"
                        value={values.nome}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        maxLength={60}
                        isInvalid={touched.nome && !!errors.nome}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.nome}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="small mb-1">Limite Arredondamento</Form.Label>
                      <Form.Control
                        type="number"
                        name="limite_arredondamento"
                        value={values.limite_arredondamento != null ? values.limite_arredondamento : ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        min={0}
                        step="0.01"
                        isInvalid={touched.limite_arredondamento && !!errors.limite_arredondamento}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.limite_arredondamento}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="small mb-1">Grupo Empresa *</Form.Label>
                      <Select
                        options={opsGE}
                        styles={selectStylesSm}
                        placeholder="Selecione..."
                        value={opsGE.find((o) => o.value === values.codigo_grupo_empresa) || null}
                        onChange={(opt) => setFieldValue('codigo_grupo_empresa', opt ? opt.value : '')}
                        onBlur={handleBlur}
                        noOptionsMessage={() => 'Nenhuma opção'}
                      />
                      {touched.codigo_grupo_empresa && errors.codigo_grupo_empresa && (
                        <div className="text-danger small mt-1">{errors.codigo_grupo_empresa}</div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="small mb-1">Status *</Form.Label>
                      <Select
                        options={STATUS_CARTAO}
                        styles={selectStylesSm}
                        placeholder="Selecione..."
                        value={STATUS_CARTAO.find((o) => o.value === values.status) || null}
                        onChange={(opt) => setFieldValue('status', opt ? opt.value : '')}
                        onBlur={handleBlur}
                        noOptionsMessage={() => 'Nenhuma opção'}
                      />
                      {touched.status && errors.status && (
                        <div className="text-danger small mt-1">{errors.status}</div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Modal.Body>

              <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
                  Cancelar
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting || loadingOpcoes}>
                  {isSubmitting ? 'Salvando...' : 'Salvar'}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      <ModalConfirmacao
        show={confirmInativacao.show}
        onClose={handleConfirmarInativacao}
        title="Inativar Cartão"
        message="Confirma inativar este cartão?"
        variant="danger"
      />
    </>
  );
};

export default ModalCartao;
